// WhyNewZealandPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyNewZealandPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'An Immigration System You Can Follow',
      description: 'Criteria are published, the points are laid out plainly and draws from the pool run to a regular rhythm. New Zealand makes an active effort to bring skilled people in.'
    },
    {
      icon: Heart,
      title: 'A Different Pace of Life',
      description: 'The landscape is remarkable, communities are safe, and work rarely swallows the rest of your week. New Zealand sits near the top of international rankings for wellbeing and quality of life.'
    },
    {
      icon: Briefcase,
      title: 'Where the Work Is',
      description: 'Employers are short of people across IT, healthcare, engineering and the skilled trades. Pay is competitive and workers are well protected in law.'
    },
    {
      icon: GraduationCap,
      title: 'Schools and Universities',
      description: 'Children of residents go through the public system at no cost, and a New Zealand qualification carries weight with employers overseas as well as at home.'
    },
    {
      icon: Shield,
      title: 'Healthcare and Support',
      description: 'Residents use the public health system, where care is free or subsidised depending on the service, with wider social support sitting behind it.'
    },
    {
      icon: TrendingUp,
      title: 'Citizenship in Time',
      description: 'Five years of residence makes you eligible to apply for citizenship, and because New Zealand allows dual citizenship you need not surrender the passport you hold.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What Draws People to New Zealand
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Applicants tend to arrive for the work and stay for the setting. The skilled migration programme opens the door; the country itself is what keeps people here.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>Worth knowing:</strong> a resident visa lets you live, work and study anywhere in New Zealand for as long as you wish. It also allows you to travel in and out of the country for a period of 2 years.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyNewZealandPR;
